import { useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Accessibility,
  ParkingSquare,
  Clock,
  Navigation,
  Map as MapIcon,
  Compass,
  Calendar,
  ArrowRight,
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "../components/ui/dialog";
import {
  CLINICAS_DETALHE,
  EQUIPA_PLACEHOLDER,
  estaAberto,
} from "../data/clinicasDetalhe";

const SCHEMA_HOURS = [
  { day: "Monday", open: "09:00", close: "13:00" },
  { day: "Monday", open: "14:00", close: "19:00" },
  { day: "Tuesday", open: "09:00", close: "13:00" },
  { day: "Tuesday", open: "14:00", close: "19:00" },
  { day: "Wednesday", open: "09:00", close: "13:00" },
  { day: "Wednesday", open: "14:00", close: "19:00" },
  { day: "Thursday", open: "09:00", close: "13:00" },
  { day: "Thursday", open: "14:00", close: "19:00" },
  { day: "Friday", open: "09:00", close: "13:00" },
  { day: "Friday", open: "14:00", close: "19:00" },
  { day: "Saturday", open: "09:00", close: "13:00" },
];

export default function ClinicaPage() {
  const { slug } = useParams();
  const clinica = CLINICAS_DETALHE[slug];
  const [lightbox, setLightbox] = useState(null);

  const aberto = useMemo(() => (clinica ? estaAberto() : null), [clinica]);

  if (!clinica) {
    return <Navigate to="/" replace />;
  }

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${clinica.lat},${clinica.lng}`;
  const appleMapsUrl = `https://maps.apple.com/?ll=${clinica.lat},${clinica.lng}&q=${encodeURIComponent(clinica.nomeOficial)}`;
  const wazeUrl = `https://www.waze.com/ul?ll=${clinica.lat}%2C${clinica.lng}&navigate=yes`;
  const embedSrc = `https://www.google.com/maps?q=${clinica.lat},${clinica.lng}&z=16&hl=pt-PT&output=embed`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VeterinaryCare",
    name: clinica.nomeOficial,
    address: {
      "@type": "PostalAddress",
      streetAddress: clinica.morada,
      postalCode: clinica.codigoPostal,
      addressLocality: clinica.cidade,
      addressRegion: "Madeira",
      addressCountry: "PT",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: clinica.lat,
      longitude: clinica.lng,
    },
    telephone: clinica.telefoneE164,
    openingHoursSpecification: SCHEMA_HOURS.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${h.day}`,
      opens: h.open,
      closes: h.close,
    })),
    priceRange: "€€",
    ...(clinica.googleRating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(clinica.googleRating),
            reviewCount: String(clinica.googleReviews),
          },
        }
      : {}),
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid={`clinica-page-${clinica.slug}`}>
      <SeoHead
        title={`${clinica.nomeOficial} | Vet Clinic`}
        description={`Cuidados veterinários de qualidade na ${clinica.cidade}. Consultas, vacinação, cirurgia e urgências. Marque online ou ligue ${clinica.telefone}.`}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden" data-testid="clinica-hero">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(800px 360px at 85% -10%, rgba(30,95,110,0.10), transparent 60%), linear-gradient(180deg, #FAF7F2 0%, #EFE9DF 100%)",
            }}
          />
          <div className="container-app relative pt-12 md:pt-16 pb-10 md:pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#88A096] hover:text-[#1E5F6E] mb-4"
              >
                ← Vet Clinic
              </Link>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-[52px] font-extrabold text-[#1A1A1A] leading-[1.05] tracking-tight">
                {clinica.nomeOficial}
              </h1>
              <p className="mt-4 text-base md:text-lg text-[#5A5A5A] max-w-xl leading-relaxed">
                A sua clínica de confiança em {clinica.cidade}.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/marcar-consulta?clinica=${clinica.slug}`}
                  data-testid="hero-marcar"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#174E5B] transition-colors"
                >
                  Marcar nesta clínica
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={`tel:${clinica.telefoneE164}`}
                  data-testid="hero-call"
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-6 py-3.5 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {clinica.telefone}
                </a>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative aspect-video rounded-3xl overflow-hidden border border-[#1E5F6E]/10 shadow-[0_30px_60px_-30px_rgba(15,58,68,0.35)]">
                <img
                  src={clinica.heroImage}
                  alt={clinica.heroAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Informações práticas */}
        <section className="container-app py-16 md:py-20" data-testid="clinica-info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Informações práticas
              </span>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Como nos encontrar e <span className="text-[#1E5F6E]">contactar</span>.
              </h2>
              <ul className="mt-8 space-y-5">
                <li className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                      Morada
                    </div>
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="info-address-link"
                      className="text-[15px] font-semibold text-[#1A1A1A] hover:text-[#1E5F6E] hover:underline underline-offset-4"
                    >
                      {clinica.morada}
                      <br />
                      {clinica.codigoPostal} {clinica.cidade}, Madeira
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                      Telefone
                    </div>
                    <a
                      href={`tel:${clinica.telefoneE164}`}
                      data-testid="info-phone-link"
                      className="text-[15px] font-semibold text-[#1E5F6E] hover:underline underline-offset-4"
                    >
                      {clinica.telefone}
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                      Email
                    </div>
                    <a
                      href={`mailto:${clinica.email}`}
                      data-testid="info-email-link"
                      className="text-[15px] font-semibold text-[#1A1A1A] hover:text-[#1E5F6E] hover:underline underline-offset-4 break-all"
                    >
                      {clinica.email}
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                    <ParkingSquare className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                      Estacionamento
                    </div>
                    <p className="text-[15px] text-[#1A1A1A]">{clinica.estacionamento}</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                    <Accessibility className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                      Acessibilidade
                    </div>
                    <p className="text-[15px] text-[#1A1A1A]">{clinica.acessibilidade}</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Horário */}
            <div>
              <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-7 md:p-9 shadow-soft">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E]">
                      <Clock className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#88A096] font-bold">
                        Horário
                      </div>
                      <div className="font-display text-xl font-bold text-[#1A1A1A]">
                        Semanal
                      </div>
                    </div>
                  </div>
                  <span
                    data-testid="status-aberto"
                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold ${
                      aberto.aberto
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-[#E8A87C]/15 text-[#7a3e1a] border border-[#E8A87C]/40"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        aberto.aberto ? "bg-emerald-500 animate-pulse" : "bg-[#E8A87C]"
                      }`}
                    />
                    {aberto.aberto ? "Aberto agora" : "Fechado"}
                  </span>
                </div>

                <table className="w-full text-[14.5px]">
                  <tbody>
                    {clinica.horario.map((d) => {
                      const isHoje = d.dia === aberto.dia.dia;
                      return (
                        <tr
                          key={d.dia}
                          data-testid={`horario-${d.curto}`}
                          className={`border-t border-[#1E5F6E]/8 ${
                            isHoje ? "bg-[#FAF7F2]" : ""
                          }`}
                        >
                          <td className={`py-3 ${isHoje ? "font-bold text-[#1E5F6E]" : "text-[#1A1A1A]"}`}>
                            {d.dia}
                            {isHoje && (
                              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#E8A87C] font-bold">
                                Hoje
                              </span>
                            )}
                          </td>
                          <td className={`py-3 text-right ${isHoje ? "font-semibold text-[#1A1A1A]" : "text-[#5A5A5A]"}`}>
                            {d.aberto ? d.blocos.join(" • ") : "Fechado"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Mapa */}
        <section className="container-app pb-16" data-testid="clinica-mapa">
          <div className="overflow-hidden rounded-3xl border border-[#1E5F6E]/10 shadow-soft">
            <iframe
              title={`Mapa - ${clinica.nomeOficial}`}
              src={embedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[400px] border-0"
            />
          </div>
        </section>

        {/* Equipa */}
        <section className="container-app py-16 md:py-20" data-testid="clinica-equipa">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
              Equipa
            </span>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] tracking-tight">
              A equipa desta <span className="text-[#1E5F6E]">clínica</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EQUIPA_PLACEHOLDER.map((m, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`team-card-${i}`}
                className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-6 md:p-8 text-center hover:shadow-soft transition-shadow"
              >
                <div className="mx-auto h-[200px] w-[200px] rounded-full bg-gradient-to-br from-[#88A096]/30 to-[#1E5F6E]/15 border border-white shadow-inner grid place-items-center">
                  <div className="font-display text-5xl font-extrabold text-[#1E5F6E]/30 select-none">
                    ?
                  </div>
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-[#1A1A1A]">
                  {m.nome}
                </h3>
                <div className="text-sm text-[#5A5A5A] mt-0.5">{m.funcao}</div>
                <p className="mt-3 text-[14px] text-[#5A5A5A] leading-relaxed">
                  {m.bio}
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                  {m.especialidades.map((e) => (
                    <span
                      key={e}
                      className="text-[11px] font-semibold uppercase tracking-wider rounded-full bg-[#1E5F6E]/8 text-[#1E5F6E] px-2.5 py-1"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* Galeria */}
        <section
          className="container-app pb-16 md:pb-24"
          data-testid="clinica-galeria"
        >
          <div className="max-w-2xl mb-10">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
              Galeria
            </span>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] tracking-tight">
              Por <span className="text-[#1E5F6E]">dentro</span> da clínica.
            </h2>
          </div>

          <Dialog
            open={!!lightbox}
            onOpenChange={(o) => !o && setLightbox(null)}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
              {clinica.galeria.map((g, i) => (
                <DialogTrigger asChild key={i}>
                  <button
                    onClick={() => setLightbox(g)}
                    data-testid={`gallery-thumb-${i}`}
                    className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-[#1E5F6E]/8 border border-[#1E5F6E]/10"
                    aria-label={g.alt}
                  >
                    <img
                      src={g.url}
                      alt={g.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F3A44]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </DialogTrigger>
              ))}
            </div>

            <DialogContent
              className="max-w-5xl bg-transparent border-0 shadow-none p-0"
              data-testid="gallery-lightbox"
            >
              <DialogTitle className="sr-only">
                {lightbox?.alt || "Galeria"}
              </DialogTitle>
              {lightbox && (
                <div className="relative">
                  <img
                    src={lightbox.url}
                    alt={lightbox.alt}
                    className="w-full max-h-[85vh] object-contain rounded-2xl bg-black"
                  />
                  <p className="mt-3 text-center text-sm text-white/80">
                    {lightbox.alt}
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </section>

        {/* Como chegar */}
        <section className="container-app pb-16" data-testid="clinica-como-chegar">
          <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-7 md:p-10 shadow-soft">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-2">
                  Como chegar
                </span>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                  Abra direções na sua aplicação preferida.
                </h3>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dir-google"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] px-5 py-3 text-sm font-bold text-white hover:bg-[#174E5B] transition-colors"
                >
                  <MapIcon className="h-4 w-4" />
                  Google Maps
                </a>
                <a
                  href={appleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dir-apple"
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-5 py-3 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
                >
                  <Compass className="h-4 w-4" />
                  Apple Maps
                </a>
                <a
                  href={wazeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dir-waze"
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-5 py-3 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
                >
                  <Navigation className="h-4 w-4" />
                  Waze
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA destacado */}
        <section className="container-app pb-20" data-testid="clinica-cta">
          <div className="relative overflow-hidden rounded-[32px] bg-[#1E5F6E] text-white px-7 md:px-14 py-14 md:py-20 text-center">
            <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
            <div className="absolute -left-12 top-1/2 -translate-y-1/2 h-60 w-60 rounded-full bg-[#88A096]/30" aria-hidden />
            <div className="absolute -right-10 -top-8 h-44 w-44 rounded-full bg-[#E8A87C]/20" aria-hidden />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-4xl lg:text-[44px] font-extrabold leading-tight tracking-tight">
                Pronto para <span className="text-[#E8A87C]">marcar?</span>
              </h2>
              <Link
                to={`/marcar-consulta?clinica=${clinica.slug}`}
                data-testid="cta-marcar-nesta-clinica"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#E8A87C] px-7 py-4 text-base font-bold text-[#1A1A1A] shadow-[0_10px_25px_-10px_rgba(232,168,124,0.7)] hover:bg-[#D99A6E] hover:-translate-y-0.5 transition-all"
              >
                <Calendar className="h-4 w-4" />
                Marcar consulta nesta clínica
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
